import React from 'react';
import * as Yup from 'yup';
import { formatDate } from '../../functions';

// Detect portal from domain
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isReadingPlex = hostname.includes('readingplex');

// MathPlex validation schema
export const validationSchema = isReadingPlex ?
    // ReadingPlex validation (with Kanban fields)
    Yup.object().shape({
        user: Yup.mixed().test(
            'userIsSelected',
            'Select a valid user',
            (value) => value?.value !== -1
        ),
        center: Yup.mixed().test(
            'centerIsSelected',
            'Select a valid center',
            (value) => value?.value !== -1
        ),
        priority: Yup.mixed().test(
            'priorityIsSelected',
            'Select a valid priority',
            (value) => value?.value !== -1 && value?.value !== ''
        ),
        notes: Yup.string()
            .max(200, 'Max 200 characters'),
        followupNotes: Yup.string()
            .max(200, 'Max 200 characters'),
        status: Yup.mixed().test(
            'statusIsSelected',
            'Select a valid status',
            (value) => value?.value !== -1
        ),
        // Kanban fields (all optional)
        projectName: Yup.string().max(200, 'Max 200 characters'), // Optional project name text field
        taskName: Yup.string()
            .max(200, 'Max 200 characters'),
        dueDate: Yup.string(), // Optional date
        assignedEmployees: Yup.array(), // Optional array
        kanbanStatus: Yup.mixed().test(
            'kanbanStatusIsSelected',
            'Select a valid Kanban status',
            (value) => value?.value !== -1 && value?.value !== ''
        ),
    }) :
    // MathPlex validation
    Yup.object().shape({
        student: Yup.mixed().test(
            'studentIsSeleceted',
            'Select a valid student',
            (value) => value?.value !== -1
        ),
        tag: Yup.mixed().test(
            'tagIsSelected',
            'Select a valid tag',
            (value) => value?.value !== -1
        ),
        center: Yup.mixed().test(
            'centerIsSelected',
            'Select a valid center',
            (value) => value?.value !== -1
        ),
        additionalNotes: Yup.string()
            .max(200, 'Max 200 characters'),
        adminNotes: Yup.string()
            .max(200, 'Max 200 characters'),
        status: Yup.mixed().test(
            'statusIsSelected',
            'Select a valid status',
            (value) => value?.value !== -1
        ),
    });

// MathPlex tag options
export const tagOptions = ['No Tag', 'Refill Lessons', 'Assign EP', 'Assessment'].map(i => ({ value: i, label: i }));
export function getInitTagOption(tag) {
    if (!tag) return tagOptions[1];
    return tagOptions.find(t => t.value === tag) || { value: -1, label: `Invalid tag (${tag})` };
}

// ReadingPlex priority options
export const priorityOptions = ['Low', 'Medium', 'High'].map(i => ({ value: i, label: i }));
export function getInitPriorityOption(priority) {
    if (!priority) return priorityOptions[0]; // Default to Low
    return priorityOptions.find(p => p.value === priority) || { value: -1, label: `Invalid priority (${priority})` };
}

export function getInitStudentOption(studentId, studentOptions) {
    if (!studentId) return { value: -1, label: 'Please select a student...' };
    return studentOptions.find(s => s.value === studentId) || { value: -1, label: `Unknown student (UID ${studentId})` };
}

export function getInitUserOption(userId, userOptions) {
    if (!userId) return { value: -1, label: 'Please select a user...' };
    return userOptions.find(u => u.value === userId) || { value: -1, label: `Unknown user (UID ${userId})` };
}

export function getInitCenterOption(center, centerOptions) {
    if (!center) return { value: -1, label: 'Please select...' };
    return centerOptions.find(c => parseInt(c.value) === parseInt(center)) || { value: -1, label: `Unknown center (ID ${center})` };
}
export function getCenterName(center, centerOptions) {
    const relCenter = centerOptions.find(c => parseInt(c.value) === parseInt(center));
    if (!relCenter) return `Unknown center (${center})`;
    else return relCenter.label;
}

// Status options - now using Kanban statuses for both status and kanbanStatus
export const statusOptions = ['To Do', 'In Progress', 'Review', 'Done'].map(i => ({ value: i, label: i }));
export function getInitStatusOption(status) {
    // Map old status values to new ones
    if (status === 'Pending') return statusOptions[0]; // 'To Do'
    if (status === 'Completed') return statusOptions[3]; // 'Done'
    if (!status) return statusOptions[0]; // Default to 'To Do'
    return statusOptions.find(s => s.value === status) || statusOptions[0];
}

// Kanban status options - same as status options now
export const kanbanStatusOptions = ['To Do', 'In Progress', 'Review', 'Done'].map(i => ({ value: i, label: i }));
export function getInitKanbanStatusOption(kanbanStatus) {
    // Map old status values to new ones
    if (kanbanStatus === 'Pending') return kanbanStatusOptions[0]; // 'To Do'
    if (kanbanStatus === 'Completed') return kanbanStatusOptions[3]; // 'Done'
    if (!kanbanStatus) return kanbanStatusOptions[0]; // Default to 'To Do'
    return kanbanStatusOptions.find(s => s.value === kanbanStatus) || kanbanStatusOptions[0];
}

// Project options helpers
export function getInitProjectOption(projectId, projectOptions) {
    if (!projectId || projectId === -1) return { value: -1, label: 'None (Standalone task)' };
    return projectOptions.find(p => parseInt(p.value) === parseInt(projectId)) || { value: -1, label: `Unknown project (ID ${projectId})` };
}

// Date helpers for due date
export function formatDateForInput(dateString) {
    if (!dateString) return '';
    // Convert 'YYYY-MM-DD' or DATETIME to 'YYYY-MM-DD' format for input
    if (typeof dateString === 'string') {
        return dateString.split(' ')[0].split('T')[0];
    }
    return '';
}

export function renderMeta(flag) {
    let created = flag.date_created ? (
        <div>Created on {formatDate(flag.date_created)} by {flag.createdByName || `Unknown user (${flag.created_by})`}</div>
    ) : null;
    let updated = flag.date_updated ? (
        <div>Updated on {formatDate(flag.date_updated)} by {flag.updatedByName || `Unknown user (${flag.updated_by})`}</div>
    ) : null;

    if (created || updated) {
        return (
            <div className="grid grid-cols-1">
                <br />
                {created}
                {updated}
            </div>
        )
    } else return null;
}